import React from "react";
import { Box } from "@mui/material";
import { AutoplayFilledRounded, EditRounded, DeleteRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import moment from "moment";
import EntityCard from "../EntityCard";
import CardActionButton from "../EntityCard/CardActionButton";
import { KeyValueInformation } from "../AssetNameplate";
import ServiceReminderStatusPill from "../ServiceReminderStatusPill";
import { getServiceReminderStatus } from "../../utils/serviceReminderStatus";
import { getAuditLine } from "../../utils/auditLine";

export interface ServiceReminderLike {
    id: string;
    serviceTaskTypeKey: string;
    defaultServiceTask?: { id: string; name?: string | null } | null;
    nextDueDate?: string | null;
    lastServiceDate?: string | null;
    insert?: string | null;
    update?: string | null;
    lastEditorUser?: { id: string; name?: string | null } | null;
}

interface Props {
    reminder: ServiceReminderLike;
    highlighted?: boolean;
    onReset: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

const RENEW_BUTTON_SX = {
    color: "#333333",
    backgroundColor: "#FDDDCD",
    "&:hover": { backgroundColor: "#FFBA99" },
};

const formatDate = (date?: string | null) => (date ? moment(date).format("DD.MM.YYYY") : undefined);

const ServiceReminderCard = ({ reminder, highlighted, onReset, onEdit, onDelete }: Props) => {
    const { t, i18n } = useTranslation();
    const status = getServiceReminderStatus(reminder.nextDueDate);
    const name = reminder.defaultServiceTask?.name || reminder.serviceTaskTypeKey;

    const auditLine = getAuditLine({
        insert: reminder.insert,
        update: reminder.update,
        editorName: reminder.lastEditorUser?.name,
        t,
        language: i18n.language,
    });

    return (
        <EntityCard
            highlighted={highlighted}
            title={name}
            tags={<ServiceReminderStatusPill status={status} nextDueDate={reminder.nextDueDate} />}
            footer={auditLine}
            actions={
                <>
                    <CardActionButton tooltip={t("Renew")} icon={<AutoplayFilledRounded />} onClick={onReset} sx={RENEW_BUTTON_SX} />
                    <CardActionButton tooltip={t("Edit")} icon={<EditRounded />} color="neutral" onClick={onEdit} />
                    <CardActionButton tooltip={t("Delete")} icon={<DeleteRounded />} color="danger" onClick={onDelete} />
                </>
            }
        >
            <Box display="flex" flexDirection="column" gap="4px">
                <KeyValueInformation title={t("Last service")} value={formatDate(reminder.lastServiceDate)} />
                <KeyValueInformation title={t("Due next")} value={formatDate(reminder.nextDueDate)} />
            </Box>
        </EntityCard>
    );
};

export default ServiceReminderCard;
